import React, { FC, memo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { WarehouseRevenueStatisticsObject } from 'entities/Warehouse';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

interface WarehouseStatisticsCardProps {
  warehouseRevenueObject: WarehouseRevenueStatisticsObject;
}

export const WarehouseStatisticsCard: FC<WarehouseStatisticsCardProps> = memo((props) => {
  const { warehouseRevenueObject } = props;
  const { t } = useAppTranslation('contracts');
  const currencySymbol = useGetCurrencySymbol();

  return (
    <div className="bg-secondaryLight rounded-lg p-4 flex flex-col max-w-[460px]">
      <div className="font-semibold text-xl mb-6">{warehouseRevenueObject.warehouseName}</div>

      <div className="bg-white rounded-lg p-6 space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Total period days')}:</span>
          <span className="font-semibold">{warehouseRevenueObject.periodDays}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Total boxes')}:</span>
          <span className="font-semibold">{warehouseRevenueObject.totalBoxes}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Occupied days')}:</span>
          <span className="font-semibold text-accent">{warehouseRevenueObject.totalOccupiedDays}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Unoccupied days')}:</span>
          <span className="font-semibold text-error">{warehouseRevenueObject.totalUnoccupiedDays}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Average occupancy')}:</span>
          <span className="font-semibold">{roundNumber(warehouseRevenueObject.averageOccupancyPercent)}%</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Total revenue')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.totalRevenue)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Revenue without discount')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.totalRevenueWithoutDiscount)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Discount amount')}:</span>
          <span className="font-semibold text-error">
            {roundNumber(warehouseRevenueObject.totalDiscountAmount)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Discounted boxes')}:</span>
          <span className="font-semibold">
            {warehouseRevenueObject.discountedBoxes} / {warehouseRevenueObject.occupiedBoxes} (
            {roundNumber(warehouseRevenueObject.discountedBoxesPercent)}%)
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Expected revenue')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.expectedRevenue)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Not invoiced yet (forecast)')}:</span>
          <span className="font-semibold">
            {roundNumber(warehouseRevenueObject.forecastRevenue)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Max possible revenue')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.maxPossibleRevenue)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Max possible revenue percent')}:</span>
          <span className="font-semibold">{roundNumber(warehouseRevenueObject.maxPossibleRevenuePercent)}%</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Average revenue per day')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.averageRevenuePerDay)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Average rate per m2')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.averageRatePerSquareMeter)} {currencySymbol}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primaryLight">{t('Average month rate per m2')}:</span>
          <span className="font-semibold text-accent">
            {roundNumber(warehouseRevenueObject.averageMonthRatePerSquareMeter)} {currencySymbol}
          </span>
        </div>
      </div>
    </div>
  );
});
